'use client';

import { useEffect } from 'react';
import { useModalStore } from '@/store';
import { resolveModalIdFromHref } from '@/utils/modal-registry';

/**
 * Turns any Prismic-authored link carrying a modal token into a store action,
 * so a CTA in any slice can open a modal without that slice knowing modals
 * exist.
 *
 * One delegated listener rather than a component threaded through the slice
 * library, for a concrete reason: there is no injection point. `@prismicio/react`
 * v3 removed `PrismicProvider`, and `PrismicNextLink` hardcodes `next/link`
 * with no component override — so the alternative would be editing ~109 slice
 * files, and even that would miss the many slices whose rich text renders plain
 * `<a>` through the default hyperlink serializer. A click listener reaches all
 * of them, and reaches slices written in the future for free.
 *
 * Capture phase is load-bearing: it runs before React's delegated handlers, so
 * `preventDefault()` here makes `next/link` bail out of navigating (its click
 * handler returns early on `defaultPrevented`).
 *
 * Called by whichever component hosts the modals — the host owns the modal, so
 * it also owns the global entry point into it.
 */
export function useModalLinkDispatcher() {
  useEffect(() => {
    // Read once: the store's actions have stable identities, so the listener
    // never needs re-installing and this hook never subscribes to state.
    const { openModal } = useModalStore.getState();

    const handleClick = (event: MouseEvent) => {
      if (event.defaultPrevented) {
        return;
      }
      if (!(event.target instanceof Element)) {
        return;
      }

      const anchor = event.target.closest('a[href]');
      const modalId = resolveModalIdFromHref(anchor?.getAttribute('href'));

      // Resolve first, act second. Every ordinary link — including the plain
      // same-page anchors used across the slice library — exits here, before
      // anything touches the event.
      if (!modalId) {
        return;
      }

      // Intercepted regardless of modifier keys: the href is an instruction,
      // not a destination, so letting a cmd-click through would only open a
      // dead-end tab.
      event.preventDefault();
      openModal(modalId);
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);
}
